import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getDeletedProjectsApi, restoreProjectApi } from "../api/api";
import "./Projects.css";

const STATUS_LABEL = {
  "todo":        "Pending",
  "in progress": "Active",
  "suspended":   "Suspended",
  "done":        "Completed",
};

const PRIORITY_LABEL = {
  "low":    "Low",
  "medium": "Medium",
  "high":   "High",
};

const ITEMS_PER_PAGE = 10;

// ── Build page numbers with ellipsis ──────────
function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages = [];
  pages.push(1);
  if (currentPage > 3) pages.push("...");
  const start = Math.max(2, currentPage - 1);
  const end   = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (currentPage < totalPages - 2) pages.push("...");
  pages.push(totalPages);
  return pages;
}

export default function DeletedProjects() {
  const navigate = useNavigate();

  const [projects,     setProjects]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [currentPage,  setCurrentPage]  = useState(1);

  const fetchDeletedProjects = async () => {
    try {
      setLoading(true);
      const response = await getDeletedProjectsApi();
      const list =
        Array.isArray(response?.data?.data) ? response.data.data :
        Array.isArray(response?.data)        ? response.data      : [];
      setProjects(list);
    } catch (err) {
      toast.error(err?.message || "Failed to load deleted projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeletedProjects(); }, []);

  // Reset to page 1 when search changes
  useEffect(() => { setCurrentPage(1); }, [search]);

  const handleRestore = async (projectId) => {
    try {
      await restoreProjectApi(projectId);
      toast.success("Project restored successfully");
      setProjects(prev => prev.filter(p => p._id !== projectId));
    } catch (err) {
      toast.error(err?.message || "Failed to restore project");
    }
  };

  const filtered = projects.filter(p => {
    const title = String(p?.title || p?.name || "");
    return !search || title.toLowerCase().includes(search.toLowerCase());
  });

  const totalPages        = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedProjects = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  if (loading) return <div className="proj-loading">Loading...</div>;

  return (
    <div className="proj-page">

      {/* ══ HEADER ══ */}
      <div className="proj-header">
        <h1 className="proj-title">Deleted Projects</h1>
        <button className="btn-add" onClick={() => navigate("/projects")}>
          ← Back to Projects
        </button>
      </div>

      {/* ══ FILTERS ══ */}
      <div className="proj-filters">
        <input
          placeholder="Search by project name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* ══ GRID ══ */}
      <div className="proj-grid">
        {filtered.length === 0 ? (
          <div className="proj-empty">No deleted projects found</div>
        ) : (
          paginatedProjects.map(p => (
            <div key={p._id} className="proj-card">
              <div className="proj-card-top">
                <span className={`dept-badge dept-${p.department}`}>{p.department}</span>
                <span className={`status-badge status-${p.status?.replace(" ", "-")}`}>
                  {STATUS_LABEL[p.status] || p.status}
                </span>
              </div>

              <div className="proj-name">{p.title || p.name}</div>
              {p.description && <div className="proj-desc">{p.description}</div>}

              {p.priority && (
                <div className="proj-priority">
                  <span className={`priority-badge priority-${p.priority}`}>
                    {PRIORITY_LABEL[p.priority] || p.priority}
                  </span>
                </div>
              )}

              <div className="proj-client">
                {p.client || p.clientName
                  ? <><span>🏢</span> {p.client?.name || p.clientName}</>
                  : <span className="no-client">No Client</span>
                }
              </div>

              <div className="proj-dates">
                {p.startDate && <span>From: {p.startDate?.slice(0, 10)}</span>}
                {p.dueDate   && <span>Until: {p.dueDate?.slice(0, 10)}</span>}
              </div>

              <div className="proj-actions">
                <button className="btn-edit" onClick={() => handleRestore(p._id)}>
                  Restore
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ══ PAGINATION ══ */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          {/* Prev */}
          <button
            className="pagination-btn pagination-arrow"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            ‹
          </button>

          {/* Page numbers */}
          {getPageNumbers(currentPage, totalPages).map((page, idx) =>
            page === "..." ? (
              <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
            ) : (
              <button
                key={page}
                className={`pagination-btn pagination-num ${currentPage === page ? "pagination-active" : ""}`}
                onClick={() => setCurrentPage(page)}
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            )
          )}

          {/* Next */}
          <button
            className="pagination-btn pagination-arrow"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            ›
          </button>

          <span className="pagination-summary">
            {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}
          </span>
        </div>
      )}

    </div>
  );
}